/**
 * Configuration service for accessing environment variables
 * This service centralizes access to all environment variables used in the application
 */

// Helper function to safely access environment variables from both Vite and Node environments
const getEnvVar = (key, defaultValue = '') => {
  // Check if import.meta is defined and has env property
  const viteValue = typeof import.meta !== 'undefined' && import.meta.env ? import.meta.env[key] : undefined;

  // Check if process is defined and has env property
  // For Firebase/Cloud Run, we need to map VITE_ prefixed keys to their non-prefixed versions
  const processKey = key.startsWith('VITE_') ? key.substring(5) : key;
  const processValue = typeof process !== 'undefined' && process.env ? process.env[processKey] : undefined;

  // Also check for the original key in process.env (some environments might keep the VITE_ prefix)
  const processOrigValue = typeof process !== 'undefined' && process.env ? process.env[key] : undefined;

  // Try to get the value from all possible sources
  let value;
  if (viteValue !== undefined) {
    value = viteValue;
  } else if (processOrigValue !== undefined) {
    value = processOrigValue;
  } else if (processValue !== undefined) {
    value = processValue;
  }

  return value !== undefined ? String(value) : defaultValue;
};

// Helper function to safely split comma-separated values
const getSplitEnvVar = (key, defaultValue = []) => {
  const value = getEnvVar(key);
  return value ? value.split(',') : defaultValue;
};

// Helper function to safely convert to number
const getNumberEnvVar = (key, defaultValue) => {
  const value = getEnvVar(key);
  if (value === undefined || value === '') return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

// Google OAuth Configuration
export const GOOGLE_CLIENT_ID = getEnvVar('BE_GOOGLE_CLIENT_ID');

// Authorization Configuration
export const AUTHORIZED_DOMAINS = getSplitEnvVar('BE_AUTHORIZED_DOMAINS');
export const AUTHORIZED_EMAILS = getSplitEnvVar('BE_AUTHORIZED_EMAILS');

// Database Configuration
export const DB_HOST = getEnvVar('BE_DB_HOST');
export const DB_USER = getEnvVar('BE_DB_USER');
export const DB_PASSWORD = getEnvVar('BE_DB_PASSWORD');
export const DB_NAME = getEnvVar('BE_DB_NAME');
export const DB_PORT = getNumberEnvVar('BE_DB_PORT', 3306);

// Validate that required environment variables are defined
const validateEnvVariables = () => {
  const requiredVars = [
    { name: 'GOOGLE_CLIENT_ID', value: GOOGLE_CLIENT_ID },
    { name: 'AUTHORIZED_DOMAINS', value: AUTHORIZED_DOMAINS },
    { name: 'AUTHORIZED_EMAILS', value: AUTHORIZED_EMAILS },
    { name: 'DB_HOST', value: DB_HOST },
    { name: 'DB_USER', value: DB_USER },
    { name: 'DB_PASSWORD', value: DB_PASSWORD },
    { name: 'DB_NAME', value: DB_NAME },
  ];

  const missingVars = requiredVars
    .filter(v => !v.value || (Array.isArray(v.value) && v.value.length === 0))
    .map(v => v.name);

  if (missingVars.length > 0) {
    console.error(`Missing required environment variables: ${missingVars.join(', ')}`);
    console.error('Please check your .env file and make sure all required variables are defined.');
  }
};

// Run validation in development mode
if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV) {
  validateEnvVariables();
}